import { useCallback, useEffect, useState } from "react";
import { AuthorizationError } from "@/shared/errors/auth-errors";
import { SessionSummaryDto, sessionDiagnosticsApi } from "@/features/session-diagnostics/api/session-diagnostics.api";

// W2 (FR-007): the mosaic re-reads its first page so a session that starts after the page
// opened appears. Bounded: every 10 s while the tab is visible, doubling on failure up to
// 60 s, and never again after a 403.
export const LIST_REFRESH_MS = 10_000;
export const LIST_REFRESH_MAX_MS = 60_000;

// 403 is opaque by design (FR-011): the page says «not available», never why.
export const loadErrorMessage = (error: unknown): { status: "error" | "forbidden"; message: string } => {
    if (error instanceof AuthorizationError) return { status: "forbidden", message: "No disponible." };
    if (error instanceof Error && error.message === "cohort_too_large") {
        return { status: "error", message: "Hay demasiados intentos en esta ventana. Acota las fechas." };
    }
    return { status: "error", message: "No se pudo cargar. Vuelve a intentarlo." };
};

// New sessions go first (the list is newest-first); a known one is updated IN PLACE, so its
// tile, its player and its share of the multiplexed poll are left as they are.
const merge = (current: SessionSummaryDto[], fresh: SessionSummaryDto[]): SessionSummaryDto[] => {
    const freshById = new Map(fresh.map((session) => [session.segmentId, session]));
    const known = new Set(current.map((session) => session.segmentId));
    const added = fresh.filter((session) => known.has(session.segmentId) === false);
    return [...added, ...current.map((session) => freshById.get(session.segmentId) ?? session)];
};

// An older page only adds what is not on screen yet.
const append = (current: SessionSummaryDto[], older: SessionSummaryDto[]): SessionSummaryDto[] => {
    const known = new Set(current.map((session) => session.segmentId));
    return [...current, ...older.filter((session) => known.has(session.segmentId) === false)];
};

export const sessionListMerge = { merge, append };

export type SessionListState =
    | { status: "loading"; message: null }
    // `latest` is the most recent first-page answer as the server gave it; `sessions` is the merged view the
    // mosaic keeps, where a session that left the first page stays with its last known state.
    | { status: "ok"; message: null; sessions: SessionSummaryDto[]; latest: SessionSummaryDto[]; nextCursor: string | null }
    | { status: "error" | "forbidden"; message: string };

export const useSessionList = (workspaceId: string | null): {
    state: SessionListState;
    more: "idle" | "loading" | "error";
    loadMore: () => void;
    reload: () => void;
} => {
    const [state, setState] = useState<SessionListState>({ status: "loading", message: null });
    const [more, setMore] = useState<"idle" | "loading" | "error">("idle");
    const [epoch, setEpoch] = useState(0);

    // A failed FIRST read is retried with the same bounded backoff as the refresh (a 403 stays final), so a
    // transient failure never disables the viewer for the life of the page.
    useEffect(() => {
        if (workspaceId === null) return;
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout> | null = null;
        let failures = 0;
        const load = (): void => {
            timer = null;
            sessionDiagnosticsApi.listSessions(workspaceId, null, controller.signal)
                .then((page) => {
                    if (controller.signal.aborted === false) setState({ status: "ok", message: null, sessions: page.sessions, latest: page.sessions, nextCursor: page.nextCursor });
                })
                .catch((error: unknown) => {
                    if (controller.signal.aborted) return;
                    setState(loadErrorMessage(error));
                    if (error instanceof AuthorizationError) return;
                    timer = setTimeout(load, Math.min(LIST_REFRESH_MS * 2 ** failures, LIST_REFRESH_MAX_MS));
                    failures += 1;
                });
        };
        setState({ status: "loading", message: null });
        load();
        return () => {
            controller.abort();
            if (timer !== null) clearTimeout(timer);
        };
    }, [workspaceId, epoch]);

    const live = state.status === "ok";

    useEffect(() => {
        if (workspaceId === null || live === false) return;
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout> | null = null;
        let failures = 0;
        let inFlight = false;
        let stopped = false;

        const clear = (): void => {
            if (timer !== null) clearTimeout(timer);
            timer = null;
        };
        const schedule = (): void => {
            if (stopped || inFlight || timer !== null || document.visibilityState === "hidden") return;
            timer = setTimeout(refresh, Math.min(LIST_REFRESH_MS * 2 ** failures, LIST_REFRESH_MAX_MS));
        };
        const refresh = (): void => {
            timer = null;
            if (stopped || inFlight) return;
            inFlight = true;
            sessionDiagnosticsApi.listSessions(workspaceId, null, controller.signal)
                .then((page) => {
                    failures = 0;
                    setState((current) => (current.status === "ok" ? { ...current, sessions: merge(current.sessions, page.sessions), latest: page.sessions } : current));
                })
                .catch((error: unknown) => {
                    if (controller.signal.aborted) return;
                    if (error instanceof AuthorizationError) {
                        stopped = true;
                        setState(loadErrorMessage(error));
                        return;
                    }
                    failures += 1;
                })
                .finally(() => {
                    inFlight = false;
                    schedule();
                });
        };
        const onVisibility = (): void => {
            clear();
            if (document.visibilityState !== "hidden") refresh();
        };

        document.addEventListener("visibilitychange", onVisibility);
        schedule();
        return () => {
            stopped = true;
            controller.abort();
            clear();
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [workspaceId, live]);

    const nextCursor = state.status === "ok" ? state.nextCursor : null;
    const loadMore = useCallback((): void => {
        if (workspaceId === null || nextCursor === null) return;
        setMore("loading");
        sessionDiagnosticsApi.listSessions(workspaceId, nextCursor)
            .then((page) => {
                setState((current) => (current.status === "ok"
                    ? { ...current, sessions: append(current.sessions, page.sessions), nextCursor: page.nextCursor }
                    : current));
                setMore("idle");
            })
            .catch(() => setMore("error"));
    }, [workspaceId, nextCursor]);

    const reload = useCallback(() => setEpoch((value) => value + 1), []);
    return { state, more, loadMore, reload };
};
